import { inject, Injectable } from "@angular/core";
import { AccountService } from "./account.service";
import { API_URL } from "./env";
import { SurveyPublic, SurveyQuestion, SurveyQuestionData } from "./models/survey";
import { fetchSafe } from "./util";
import { SurveyType } from "./models/survey";

@Injectable()
export class SurveyService {
  account = inject(AccountService);

  surveys: Record<number, SurveyPublic> = {};

  autofill: Record<string, string | number> = {};

  private readonly longitudinalSurveys: SurveyType[] = [SurveyType.COST, SurveyType.SDoH, SurveyType.CHLT6];

  costScore!: number | null;
  costCompleted = false;

  async fetchSurveys() {
    const [surveys] = await fetchSafe<{ surveys: SurveyPublic[] }>(API_URL + "/survey/list");

    // index to idetify where the COST survey is
    let costSurveyIndex = 0;

    if (surveys === null) {
      await this.account.logout();
      return;
    }

    // loop through all surveys for a user
    for (const [_index, i] of surveys.surveys.entries()) {
      // only use the last survey of each type - if a user takes COST multiple times, only pull back the most recent
      this.surveys[i.id] = i;

      // update index to point to where COST survey is
      if (i.name == "COST") {
        costSurveyIndex = i.id;
      }
    }

    // Get score from most recent COST survey. A score of 0 indicates that a user completed the survey but scored 0, not that the survey was not completed.
    // Take care in handling this.
    const potentialScore = this.surveys[costSurveyIndex].score;

    if (potentialScore != null) {
      this.costCompleted = true;
      this.costScore = potentialScore;
    } else {
      this.costCompleted = false;
    }
  }

  async fetchQuestions(surveyId: number): Promise<SurveyQuestion[]> {

    if (Object.keys(this.surveys).length === 0) {
      // We forgot what surveys existed, fetch it again
      await this.fetchSurveys();
    }

    if (!(surveyId in this.surveys)) {
      await this.account.logout();
      return [];
    }

    const [response] = await fetchSafe<{
      questions: SurveyQuestion[];
    }>(API_URL + "/survey/list_questions", {
      body: JSON.stringify({ survey_id: surveyId }),
    });

    if (response === null) {
      await this.account.logout();
      return [];
    }

    // Sort by seq
    response.questions.sort((a, b) => (a.seq ?? 9999) - (b.seq ?? 9999));

    // Decode JSON data
    for (const val of response.questions) {
      val.props = JSON.parse(val.data ?? "{}") as SurveyQuestionData;
    }

    // Bind to their corresponding survey
    this.surveys[surveyId].questions = response.questions;

    return response.questions;
  }

  // Whether all surveys have been completed
  hasRecommendations(): boolean {
    return (
      1 in this.surveys &&
      Object.entries(this.surveys).every((value: [string, SurveyPublic]) => {
        // Check if survey submitted previously (date tied to it)
        return value[1].submitted_date != null;
      })
    );
  }

  hasCompleted(survey: SurveyPublic): boolean {
    return survey.submitted_date != null;
  }

  /**
   * Checks to see if the survey is categorized as longitudinal.
   * @param surveyId
   * @returns {boolean} true if survey is longitudinal
   */
  public surveyLongitudinal(surveyId: number): boolean {
    if (this.longitudinalSurveys.includes(surveyId)) {
      return true;
    }
    else {
      return false;
    }
  }
}
